/* AIAssistant.js */
/* eslint react-hooks/exhaustive-deps: 0 */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  Tooltip,
  MultiSelect,
  Toggle,
} from '@carbon/react';
import LogoImage from './../../main/logos/ffailogo1.png';
import { authFetch } from '../../utils/authFetch';
import {
  ArrowRight,
  Demo,
  Information,
  VolumeUp,
  VolumeMute,
} from '@carbon/react/icons';
import ReactMarkdown from 'react-markdown';
import JEmoji from 'emoji-toolkit';
import { v4 as uuidv4 } from 'uuid';
import { modelInfo } from './ModelInfo';

// Replace the github import with a dark theme
import { atomOneDark } from 'react-syntax-highlighter/dist/cjs/styles/hljs';

import remarkGfm from 'remark-gfm';
// Syntax highlighter (using highlight.js via react-syntax-highlighter)
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { ModelSelector } from './ModelSelector';
//import ToolSelector, { BUILTIN_FAMILIES } from './ToolSelector';
import ToolSelectorModal, { BUILTIN_FAMILIES } from './ToolSelectorModal';
import { speak, stopEx } from '../../utils/speechUtils';
import SpeechInput from '../../components/SpeechInput/SpeechInput';

// Common languages
import jsLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/javascript';
import pythonLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/python';
import bashLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/bash';
import phpLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/php';
import rubyLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/ruby';
import javaLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/java';
import cLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/c';
import cppLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/cpp';
import csharpLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/csharp';
import goLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/go';
import rustLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/rust';
import swiftLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/swift';

// Web-related
import cssLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/css';
import tsLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/typescript';
import xmlLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/xml';

// Scripting/config
import perlLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/perl';
import sqlLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/sql';
import jsonLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/json';
import yamlLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/yaml';
import iniLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/ini';

// Additional languages
import kotlinLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/kotlin';
import scalaLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/scala';
import rLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/r';
import psLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/powershell';
import groovyLanguage from 'react-syntax-highlighter/dist/cjs/languages/hljs/groovy';

import { fetchPreferences } from './api';

// Register languages for highlight.js (add more as needed)
SyntaxHighlighter.registerLanguage('javascript', jsLanguage);
SyntaxHighlighter.registerLanguage('python', pythonLanguage);
SyntaxHighlighter.registerLanguage('bash', bashLanguage);
SyntaxHighlighter.registerLanguage('shell', bashLanguage);
SyntaxHighlighter.registerLanguage('php', phpLanguage);
SyntaxHighlighter.registerLanguage('ruby', rubyLanguage);
SyntaxHighlighter.registerLanguage('java', javaLanguage);
SyntaxHighlighter.registerLanguage('c', cLanguage);
SyntaxHighlighter.registerLanguage('cpp', cppLanguage); // C++
SyntaxHighlighter.registerLanguage('csharp', csharpLanguage); // C#
SyntaxHighlighter.registerLanguage('go', goLanguage);
SyntaxHighlighter.registerLanguage('rust', rustLanguage);
SyntaxHighlighter.registerLanguage('swift', swiftLanguage);

// Web-related
SyntaxHighlighter.registerLanguage('css', cssLanguage);
SyntaxHighlighter.registerLanguage('typescript', tsLanguage);
SyntaxHighlighter.registerLanguage('xml', xmlLanguage);

// Scripting/config
SyntaxHighlighter.registerLanguage('perl', perlLanguage);
SyntaxHighlighter.registerLanguage('sql', sqlLanguage);
SyntaxHighlighter.registerLanguage('json', jsonLanguage);
SyntaxHighlighter.registerLanguage('yaml', yamlLanguage);
SyntaxHighlighter.registerLanguage('ini', iniLanguage);

// Additional languages
SyntaxHighlighter.registerLanguage('kotlin', kotlinLanguage);
SyntaxHighlighter.registerLanguage('scala', scalaLanguage);
SyntaxHighlighter.registerLanguage('r', rLanguage);
SyntaxHighlighter.registerLanguage('powershell', psLanguage); // PowerShell
SyntaxHighlighter.registerLanguage('groovy', groovyLanguage);

const availableTools = {
  web_search_google: 'Web Search (Google)',
  news_search_google: 'News Search (Google)',
  web_search_tavily: 'Web Search (Tavily)',
  medical_search_pubmed: 'Medical Search (PubMed)',
  reddit_search: 'Reddit Search',
  jobs_search_google: 'Job Search (Google)',
  web_trends_search_google: 'Web Trends Search (Google)',
  scholarly_search_google: 'Scholarly Search (Google)',
  finance_news_search_yahoo: 'Finance News Search (Yahoo)',
  finance_news_search_google: 'Finance News Search (Google)',
  video_search_youtube: 'Video Search (YouTube)',
  programming_search_stackoverflow: 'Programming Search (StackOverflow)',
  encyclopedia_search_wikipedia: 'Encyclopedia Search (Wikipedia)',
  finance_news_search_alphavantage: 'Finance News Search (AlphaVantage)',
  web_search_duckduckgo: 'Web Search (DuckDuckGo)',
  news_search_duckduckgo: 'News Search (DuckDuckGo)',
  goal_search: 'Persistent Search',
};

const BUILTIN_TOOL_IDS = new Set(
  Object.values(BUILTIN_FAMILIES).flatMap(f => Object.keys(f.tools))
);

const AIAssistant = ({
  token,
  user,
  placeholderText,
  propInput,
  setInput,
  propConversationId,
  setConversationId,
  propConversationHistory,
  setConversationHistory,
  propResponse,
  setResponse,
}) => {
  /****************
   *  LOCAL STATE *
   ****************/
  const input = propInput;
  const conversationId = propConversationId;
  const conversationHistory = propConversationHistory;
  const response = propResponse;
  const technicalSpecsStorageKey = 'AI_showTechnicalSpecs';

  const [currentPlaceholderText, setCurrentPlaceholderText] = useState(
    placeholderText
  );

  useEffect(() => {
    setCurrentPlaceholderText(placeholderText);
  }, [placeholderText]);

  const [selectedModel, setSelectedModel] = useState(() => {
    return localStorage.getItem('selectedModel') || 'gpt-5-mini';
  });

  const [rememberConversation, setRememberConversation] = useState(true);
  const [useTools, setUseTools] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTools, setSelectedTools] = useState([
    'web_search_google',
    'web_search_tavily',
    'news_search_google',
    'web_search_duckduckgo',
    'reddit_search',
    'news_search_duckduckgo',
    'reddit_search',
    'medical_search_pubmed',
    'scholarly_search_google',
    'finance_news_search_yahoo',
    'finance_news_search_google',
    'video_search_youtube',
    'programming_search_stackoverflow',
    'encyclopedia_search_wikipedia',
    'finance_news_search_alphavantage',
  ]);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [mcpServers, setMcpServers] = useState([]); // [{id,name},…]

  /* filteredModels, searchQuery, showTechnicalSpecs moved to ModelSelector internal state */

  /**************************
   *  MEMO / CALLBACK HOOKS *
   **************************/

  const initialRender = useRef(true);

  useEffect(() => {
    authFetch('/mlapi/premium/mcp-servers', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : []))
      .then(list => setMcpServers(list)) // keep full objects
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    fetchPreferences(token)
      .then(p => {
        setSelectedTools(p.tools.selected_tools);
        setSelectedFamilies(p.tools.selected_families);
        /* if you have a “prompt” input:  setDefaultPrompt(p.llm_prompt || ""); */
      })
      .catch(() => {
        /* first visit: ignore */
      });
  }, [token]);

  /* focus the search box in the model‑picker */

  /* persist model selection */
  useEffect(() => {
    localStorage.setItem('selectedModel', selectedModel);
  }, [selectedModel]);

  /* WebSocket: receive AI tab completion notifications when HTTP stream drops
     mid-tool (e.g. execute_code). The backend drains LangGraph in the background
     and publishes the final AI response on the agent_updates channel. */
  useEffect(() => {
    if (!token || !user) return;
    const { protocol, host } = window.location;
    const wsUrl = `${
      protocol === 'https:' ? 'wss:' : 'ws:'
    }//${host}/mlapi/premium/agent/ws/updates/${user}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = e => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'ai_tab_completion' && msg.text) {
            setResponse(prev => prev + msg.text);
            // Append to last history entry so the accordion shows the full
            // response, not just the partial text captured before the HTTP drop.
            setConversationHistory(prev => {
              if (prev.length === 0) return prev;
              const updated = [...prev];
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                answer: updated[updated.length - 1].answer + msg.text,
              };
              return updated;
            });
          }
        } catch (_) {
          /* non-JSON agent update — ignore */
        }
      };
      ws.onerror = e => console.debug('AI tab WS error:', e);
    } catch (e) {
      console.debug('AI tab WS connect error:', e);
    }
    return () => {
      if (ws) ws.close();
    };
  }, [token, user, setResponse, setConversationHistory]);

  /* -------- Performance helpers -------- */
  const handleInputChange = useCallback(
    e => {
      setInput(e.target.value);
    },
    [setInput]
  );

  const formattedResponseMemo = useMemo(
    () => JEmoji.shortnameToUnicode(response),
    [response]
  );

  const handleClearHistory = () => {
    setConversationHistory([]); // Clears the conversation history
    setResponse(''); // Optionally clear the current response display
    setConversationId(uuidv4()); // Generate a new conversation ID
    setCurrentPlaceholderText('');
    setIsLoading(false);
  };

  /* ---------------- Re‑ask helpers ---------------- */
  /**
   * Trim history up to (but *excluding*) the index to re‑ask,
   * set a new UUID, and optionally auto‑submit using the trimmed
   * history and fresh conversation‑ID.
   */
  const reaskAtIndex = (idx, autoSubmit = false) => {
    if (idx < 0 || idx >= conversationHistory.length) return;

    const trimmedHistory = conversationHistory.slice(0, idx);
    const question = conversationHistory[idx].question;
    const newId = uuidv4();

    setConversationId(newId);
    setConversationHistory(trimmedHistory);
    setResponse('');
    setInput(autoSubmit ? '' : question);
    setCurrentPlaceholderText('');
    setIsLoading(false);

    setTimeout(() => {
      if (autoSubmit) {
        handleSubmit(question, trimmedHistory, newId);
      } else {
        document.querySelector('.llm-request-textarea')?.focus();
      }
    }, 0);
  };

  const renderCodeBlock = ({ inline, className, children }) => {
    const codeText = String(children).replace(/\n$/, '');
    const languageMatch = /language-(\w+)/.exec(className || '');
    const language = languageMatch ? languageMatch[1] : null;
    const effectiveLanguage = language || 'plaintext';

    if (inline) return <code className="inline-code">{children}</code>;

    if (effectiveLanguage === 'plaintext') {
      return (
        <pre className="plaintext-pre">
          <code className="plaintext-code">{codeText}</code>
        </pre>
      );
    }
    return (
      <div className="code-block-container">
        <div className="code-block-header">
          <span className="language-label">{effectiveLanguage}</span>
          <button
            className="copy-button"
            onClick={() => navigator.clipboard.writeText(codeText)}>
            📋 Copy
          </button>
        </div>
        <SyntaxHighlighter
          style={atomOneDark}
          language={effectiveLanguage}
          PreTag="div"
          className="syntax-highlighter">
          {codeText}
        </SyntaxHighlighter>
      </div>
    );
  };

  /* ---------------- State ---------------- */
  const [voiceMode, setVoiceMode] = useState(false);
  useEffect(() => {
    return () => stopEx();
  }, []);

  /* ---------------- HISTORY ACCORDION ---------------- */
  const historyAccordionMemo = useMemo(() => {
    console.time('render-history');
    const lastInteractionIdx = conversationHistory.length - 1;
    const lastInteraction =
      lastInteractionIdx >= 0 ? conversationHistory[lastInteractionIdx] : null;

    const accordion =
      conversationHistory.length > 0 ? (
        <>
          <div className="history-actions">
            <Button
              kind="ghost"
              size="sm"
              onClick={() => reaskAtIndex(lastInteractionIdx, false)}
              aria-label={`Re-ask: ${lastInteraction?.question}`}>
              Re-ask with different model
            </Button>
            <Button
              kind="ghost"
              size="sm"
              onClick={() => reaskAtIndex(lastInteractionIdx, true)}
              aria-label="Quick re-ask with current model">
              Re-ask with {modelInfo[selectedModel]?.name}
            </Button>
          </div>
          <h4>Conversation History</h4>
          <Accordion>
            {conversationHistory.map((interaction, index) => (
              <AccordionItem
                title={`Q: ${(interaction.question || '').slice(0, 120)}…`}
                key={index}>
                <div className="history-actions">
                  <Button
                    kind="ghost"
                    size="sm"
                    onClick={() => reaskAtIndex(index, false)}
                    aria-label={`Re-ask: ${interaction.question}`}>
                    Re-ask with different model
                  </Button>
                  <Button
                    kind="ghost"
                    size="sm"
                    onClick={() => reaskAtIndex(index, true)}
                    aria-label="Quick re-ask with current model">
                    Re-ask with {modelInfo[selectedModel]?.name}
                  </Button>
                </div>

                <div className="llm-response-area">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{ code: renderCodeBlock }}>
                    {interaction.answer}
                  </ReactMarkdown>
                  <div className="model-meta">
                    <small>
                      Answered by:{' '}
                      {modelInfo[interaction.model]?.name || 'AI Assistant'}
                    </small>
                  </div>
                </div>
              </AccordionItem>
            ))}
          </Accordion>
        </>
      ) : null;
    console.timeEnd('render-history');
    return accordion;
  }, [conversationHistory, selectedModel]);

  /***********************
   *  EVENT HANDLERS etc *
   ***********************/
  const availableToolsArray = Object.entries(
    availableTools
  ).map(([id, label]) => ({ id, label }));

  const isReasking = () => {
    return conversationHistory.some(
      q => q.question === input && q.model !== selectedModel
    );
  };

  /*
  const renderCodeBlockLight= ({ inline, className, children, ...props }) => {
    const codeText = String(children).replace(/\n$/, '');
    const languageMatch = /language-(\w+)/.exec(className || '');  
    const language = languageMatch ? languageMatch[1] : null;
    if (inline) {
      // Inline code within a sentence, just render a <code> tag
      return <code className="inline-code">{children}</code>;
    }
    return (
      <div className="code-block">
        <SyntaxHighlighter style={codeStyle} language={language || 'plaintext'} PreTag="div">
          {codeText}
        </SyntaxHighlighter>
        <button className="copy-button" onClick={() => navigator.clipboard.writeText(codeText)}>
          Copy
        </button>
      </div>
    );
  };
  */

  /**
   * Submit a question to the backend.
   *  – overrideInput …… string to use instead of current `input`
   *  – overrideHistory … array to use instead of `conversationHistory`
   *  – overrideConvId … conversation‑id to use for this request
   */
  const handleSubmit = async (
    overrideInput = null,
    overrideHistory = null,
    overrideConvId = null
  ) => {
    const currentInput = overrideInput ?? input;
    const history = Array.isArray(overrideHistory)
      ? overrideHistory
      : conversationHistory;
    const convId = overrideConvId ?? conversationId;

    const reaskingNow = overrideConvId
      ? false
      : history.some(
          h => h.question === currentInput && h.model !== selectedModel
        );
    const conversationIdForThisRun =
      reaskingNow && !overrideConvId ? uuidv4() : convId;
    if (reaskingNow && !overrideConvId)
      setConversationId(conversationIdForThisRun);

    setIsLoading(true);

    /* ① expand built-in families */
    const builtInFromFamilies = selectedFamilies
      .filter(f => !f.startsWith('mcp_')) // only FFAI families
      .flatMap(f => BUILTIN_FAMILIES[f]?.tools || []);

    /* ② split selectedTools into built-in vs MCP */
    const builtInList = selectedTools.filter(t => BUILTIN_TOOL_IDS.has(t));
    const mcpToolNames = selectedTools.filter(t => !BUILTIN_TOOL_IDS.has(t));

    /* ③ combine all built-in IDs (dedup) */
    const allBuiltIns = Array.from(
      new Set([...builtInList, ...builtInFromFamilies])
    );

    /* query strings */
    // only ids that FastAPI’s Enum accepts
    const ENUM_SAFE_IDS = new Set([
      'web_search_google',
      'news_search_google',
      'web_search_tavily',
      'medical_search_pubmed',
      'reddit_search',
      'jobs_search_google',
      'web_trends_search_google',
      'scholarly_search_google',
      'finance_news_search_yahoo',
      'finance_news_search_google',
      'video_search_youtube',
      'programming_search_stackoverflow',
      'encyclopedia_search_wikipedia',
      'finance_news_search_alphavantage',
      'web_search_duckduckgo',
      'news_search_duckduckgo',
      'goal_search',
    ]);

    const toolQs = allBuiltIns
      .filter(id => ENUM_SAFE_IDS.has(id))
      .map(id => `tools=${encodeURIComponent(id)}`)
      .join('&');

    // ① families coming from MCP selector
    const famQsFamilies = selectedFamilies
      .filter(f => f.startsWith('mcp_')) // our ToolSelector prefixes MCP families
      .map(
        f =>
          `mcp_tools=${encodeURIComponent(
            f
              .split('_')
              .slice(2)
              .join('_')
          )}`
      ) // strip prefix
      .join('&');

    // ② individual MCP tools
    const mcpQsNames = mcpToolNames
      .map(t => `mcp_tools=${encodeURIComponent(t)}`)
      .join('&');

    const serverQs = mcpServers
      .map(s => `mcp_servers=${encodeURIComponent(s.id)}`)
      .join('&');

    const rememberQs = rememberConversation ? '' : 'remember=false';

    const fullQs = [toolQs, famQsFamilies, mcpQsNames, serverQs, rememberQs]
      .filter(Boolean)
      .join('&');

    const nowIso = new Date().toISOString();
    let prompt = `(Please format your answer in Markdown, using headings, lists, tables. Use fenced code blocks as appropriate if it is a coding question. The date and time is currently: ${nowIso}) ${currentInput}`;

    // Inject Voice Mode prompt if enabled
    if (voiceMode) {
      prompt +=
        ' (SYSTEM: The user is in eyes-free voice mode. Keep response short, conversational, plain text only. No markdown tables or code blocks.)';
    }

    const roleForAIResponse =
      selectedModel === 'gemini-pro' ? 'model' : 'system';

    const messages = history.flatMap(interaction => [
      { role: 'user', content: interaction.question },
      { role: roleForAIResponse, content: interaction.answer },
    ]);
    messages.push({ role: 'user', content: prompt });

    // Build the tools query parameters with repeated `tools=` syntax
    const toolsQueryParams = selectedTools
      .map(tool => `tools=${encodeURIComponent(tool)}`)
      .join('&');

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(messages),
    };

    setResponse(`\n  \n**${currentInput}**\n  \n  `);
    if (!overrideInput) setInput('');

    try {
      const fetchResp = await authFetch(
        `/mlapi/basic/${
          useTools
            ? `stream-llm-tools?model=${selectedModel}&${fullQs}`
            : `stream-llm?model=${selectedModel}`
        }`,
        requestOptions
      );

      if (!fetchResp.ok) {
        // authFetch already dispatched ffai-auth-failed for 401/403; bail out so
        // the error JSON body doesn't get streamed into the chat as a message.
        setIsLoading(false);
        setResponse('');
        return;
      }

      if (!fetchResp.body) throw new Error('Response body is not readable');

      const reader = fetchResp.body.getReader();
      let receivedLength = 0;
      const chunks = [];
      let firstChunkReceived = false;
      let acc = ''; // Accumulate the full response text

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;

        const textChunk = new TextDecoder('utf-8')
          .decode(value, {
            stream: true,
          })
          .replace('[DONE]', '');
        acc += textChunk; // Accumulate chunk

        if (!firstChunkReceived) {
          setIsLoading(false);
          firstChunkReceived = true;
        }

        setResponse(prevResponse => prevResponse + textChunk);
      }

      const chunksAll = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }

      const resultText = new TextDecoder('utf-8')
        .decode(chunksAll)
        .replace('[DONE]', '');
      setResponse(`\n  \n**${currentInput}**\n  \n  ${resultText}`);

      setConversationHistory(prevHistory => [
        ...prevHistory,
        {
          question: currentInput,
          answer: resultText,
          model: selectedModel,
        },
      ]);

      // Speak the result if in voice mode
      if (voiceMode) {
        speak(resultText);
      }

      if (rememberConversation) {
        const latest_messages = [
          { role: 'user', content: currentInput },
          { role: 'system', content: resultText },
        ];
        try {
          await updateConversation(conversationIdForThisRun, latest_messages);
        } catch (error) {
          console.error('Error during updateConversation API call:', error);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      // Stream dropped mid-response (e.g. execute_code running). Save whatever
      // content we received so the history entry exists for the WS notification
      // to update when the backend drain completes.
      if (acc) {
        setConversationHistory(prevHistory => [
          ...prevHistory,
          { question: currentInput, answer: acc, model: selectedModel },
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  async function updateConversation(conversationIdToSave, messages) {
    console.log('Saving conversation history to elasticsearch');
    const url = `/mlapi/basic/conversation/${encodeURIComponent(
      conversationIdToSave
    )}/update`;

    const payload = {
      messages,
      content_type: 'ai_conversation',
      model_id: selectedModel,
    };

    try {
      const apiResp = await authFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!apiResp.ok) throw new Error(`HTTP error! Status: ${apiResp.status}`);

      return await apiResp.json();
    } catch (error) {
      console.error('Error during updateConversation API call:', error);
      throw error;
    }
  }

  function truncateToTwoSentences(text) {
    if (text.length < 420) {
      const hasMarkup = /[<>{}[\];]/;
      const idx = text.search(hasMarkup);
      if (idx > 160) return text.substr(0, idx) + '...';
    }
    const sentences = text.match(/[^.!?]+[.!?]+/g);
    return sentences && sentences.length > 2
      ? sentences.slice(0, 2).join(' ') + '...'
      : text;
  }

  const handleToolChange = event => {
    const newSelectedItems = event.selectedItems
      ? event.selectedItems.map(item => item.id)
      : [];

    setSelectedTools(prevSelectedTools => {
      if (
        !Array.isArray(prevSelectedTools) ||
        JSON.stringify(prevSelectedTools) !== JSON.stringify(newSelectedItems)
      ) {
        return newSelectedItems;
      }
      return prevSelectedTools;
    });
  };

  const formattedResponse = JEmoji.shortnameToUnicode(response);

  return (
    <>
      {/* ——— TOOLBAR / MODEL SELECTOR ——— */}
      <div className="toolbar-section">
        <ModelSelector
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          isModelModalOpen={isModelModalOpen}
          setIsModelModalOpen={setIsModelModalOpen}
          techSpecsStorageKey={technicalSpecsStorageKey}
          legacyStorageKey="AI_showLegacyModels"
        />
        <ToolSelectorModal
          token={token}
          selectedTools={selectedTools}
          setSelectedTools={setSelectedTools}
          selectedFamilies={selectedFamilies}
          setSelectedFamilies={setSelectedFamilies}
          mcpServers={mcpServers}
        />
      </div>
      {/* ——— INPUT AREA ——— */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '0.5rem',
          marginBottom: '0.5rem',
        }}>
        <div style={{ flex: 1 }}>
          <textarea
            className="llm-request-textarea"
            placeholder={currentPlaceholderText}
            value={input}
            onChange={handleInputChange}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSubmit();
              }
            }}
            style={{ marginBottom: 0 }}
          />
        </div>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <SpeechInput
            onTranscript={text =>
              setInput(prev => prev + (prev ? ' ' : '') + text)
            }
            onInterim={text => {
              /* Optional: show interim text somewhere or just ignore */
            }}
            onSubmit={text => {
              const fullText = input + (input ? ' ' : '') + text;
              setInput(fullText);
              handleSubmit(fullText);
              setInput('');
            }}
          />
          <Tooltip
            label={
              voiceMode
                ? 'Voice Mode ON: Responses will be spoken'
                : 'Enable Voice Mode'
            }
            align="bottom-right">
            <Button
              hasIconOnly
              renderIcon={voiceMode ? VolumeUp : VolumeMute}
              iconDescription={
                voiceMode ? 'Disable Voice Mode' : 'Enable Voice Mode'
              }
              kind="ghost"
              size="sm"
              onClick={() => {
                const newMode = !voiceMode;
                setVoiceMode(newMode);
                if (!newMode) stopEx(); // Stop talking if we turn it off
              }}
              style={{ color: voiceMode ? '#0f62fe' : undefined }}
            />
          </Tooltip>
        </div>
      </div>
      {/* Remember checkbox */}
      <input
        type="checkbox"
        id="rememberConversation"
        name="rememberConversation"
        checked={rememberConversation}
        onChange={e => setRememberConversation(e.target.checked)}
      />
      <label htmlFor="rememberConversation">Remember</label>&nbsp;
      <Tooltip
        label="Select Remember to save the conversation so you may review or continue it in the future…"
        align="bottom">
        <button className="sb-tooltip-trigger" type="button">
          <Information />
        </button>
      </Tooltip>
      &nbsp;&nbsp; &nbsp;&nbsp;
      <Tooltip
        label="Watch a 2-minute video tutorial about this AI page"
        align="bottom">
        <Button
          kind="ghost"
          size="sm"
          onClick={() =>
            window.open('https://youtu.be/ZuxriI3ISYI', '_blank', 'noopener')
          }
          className="tooltip-button"
          aria-label="Watch tutorial video">
          <Demo />
        </Button>
      </Tooltip>
      {/* ——— ACTION BUTTONS ——— */}
      <div className="action-buttons">
        <Button
          className="llm-submit"
          kind="primary"
          size="small"
          disabled={isLoading}
          onClick={() => handleSubmit()}
          aria-label={
            isReasking()
              ? `Re-ask with ${modelInfo[selectedModel]?.name}`
              : 'Ask question'
          }>
          {isReasking() ? (
            <>
              Re-ask with {modelInfo[selectedModel]?.name} <ArrowRight />
            </>
          ) : (
            <>
              Ask <ArrowRight />
            </>
          )}
        </Button>
        <Button
          className="llm-submit"
          kind="secondary"
          size="small"
          onClick={handleClearHistory}>
          Clear
        </Button>
      </div>
      {/* ——— RESPONSE AREA ——— */}
      {isLoading ? (
        <>
          <div className="llm-response-area">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{ code: renderCodeBlock }}>
              {formattedResponseMemo}
            </ReactMarkdown>
            <div className="typing-indicator">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        </>
      ) : (
        <div className="llm-response-area">
          {formattedResponseMemo && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{ code: renderCodeBlock }}>
              {formattedResponseMemo}
            </ReactMarkdown>
          )}
          {conversationHistory.length === 0 && !formattedResponseMemo && (
            <img
              className="landing-page__illo"
              src={LogoImage}
              alt="DLT Logo"
            />
          )}
        </div>
      )}
      {/* ——— HISTORY ACCORDION ——— */}
      <div className="accordion-top-space">{historyAccordionMemo}</div>
    </>
  );
};

export default AIAssistant;
